import { Modal, Button as PLButton } from '@payaca/ambrosia';
import { isEqual } from 'lodash-es';
import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobsActions from '@payaca/store/jobs/jobsActions';
import * as lineItemGroupActions from '@payaca/store/lineItemGroups/lineItemGroupsActions';

import useUpdateProposalPaymentSchedule from '@/api/mutations/proposals/useUpdateProposalPaymentSchedule';
import { useGetProposalPaymentSchedule } from '@/api/queries/proposal/useGetProposal';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import BodyWithSidePanelContentWrapper from '../bodyWithSidePanelContentWrapper/BodyWithSidePanelContentWrapper';
import JobAdditionalNotesControl from '../editJob/JobAdditionalNotesControl';
import JobCustomerControl from '../editJob/JobCustomerControl';
import JobIntroControl from '../editJob/JobIntroControl';
import JobPromoAttachmentsControl from '../editJob/JobPromoAttachmentsControl';
import JobReferenceControl from '../editJob/JobReferenceControl';
import EditJobPreviewButton from '../editJobPreviewButton/EditJobPreviewButton';
import EditJobSaveFeedback from '../editJobSaveFeedback/EditJobSaveFeedback';
import InvoiceLineItemsControl from '../invoiceLineItemsControl/InvoiceLineItemsControl';
import JobLineItemGroupsControl from '../jobLineItemGroupsControl/JobLineItemGroupsControl';
import JobSidePanel from '../jobSidePanel/JobSidePanel';

import { Deal } from '@payaca/types/dealTypes';
import { UpdateJobContentRequestData } from '@payaca/types/jobContentRequestTypes';
import { JobContent } from '@payaca/types/jobContentTypes';
import { PartialUpdateJobRequestData } from '@payaca/types/jobRequestTypes';
import { Job } from '@payaca/types/jobTypesV2';

import { isInvoice } from '@/helpers/jobHelper';
import { canSetCustomerForDeal } from '@payaca/helpers/dealHelper';
import {
  getInitialUpdateJobContentRequestDataFromBaseJobContent,
  getJobRequiresUpdate,
} from '@payaca/helpers/jobHelperV2';

import {
  getDealByJobId,
  getJob,
  getJobContent,
  getJobsByDealId,
} from '@/utils/stateAccessors';

import { useSelector } from '@/api/state';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import PaymentScheduleSummary from '@payaca/components/paymentScheduleSummary/PaymentScheduleSummary';

import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import {
  getPaymentScheduleFieldValidator,
  isPaymentScheduleValid,
} from '@payaca/helpers/paymentScheduleHelper';
import { useCounter } from '@payaca/hooks';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { useDeal } from '@payaca/store/hooks/appState';
import { ReadableJobStatus } from '@payaca/types/jobTypes';
import { PaymentSchedule } from '@payaca/types/payment-schedule';
import CreatePaymentScheduleControl, {
  PaymentScheduleInput,
} from '../createEditProposalTemplateControl/CreatePaymentScheduleControl';
import EditJobSection from '../editJob/EditJobSection';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ApplyTemplateModal from './ApplyTemplateModal';
import './EditJobControl.sass';

type Props = {
  jobId: number;
  proceedToPreview: () => void;
};

const EditJobControl: FC<Props> = ({
  jobId,
  proceedToPreview,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();

  // oxlint-disable-next-line typescript-eslint(no-redundant-type-constituents) -- FIXME: pre-existing error
  const job: Job | undefined = useSelector((state) => getJob(state, jobId));

  // oxlint-disable-next-line typescript-eslint(no-redundant-type-constituents) -- FIXME: pre-existing error
  const jobContent: JobContent | undefined = useSelector((state) => {
    if (!job?.jobContentId) return;
    return getJobContent(state, job.jobContentId);
  });

  // oxlint-disable-next-line typescript-eslint(no-redundant-type-constituents) -- FIXME: pre-existing error
  const deal: Deal | undefined = useSelector((state) =>
    getDealByJobId(state, jobId)
  );

  const isUpdatingJob: boolean = useSelector((state) => {
    return state.jobsStore.isUpdatingJob;
  });

  const isFetchingJob: boolean = useSelector((state) => {
    return state.jobsStore.jobs && state.jobsStore.jobs[jobId]?.isFetching;
  });

  const isUpdatingJobContent: boolean = useSelector((state) => {
    return state.jobContent.isUpdatingJobContent;
  });

  const isFetchingJobContent: boolean = useSelector((state) => {
    if (!job?.jobContentId) return false;
    return (
      state.jobContent.jobContents &&
      state.jobContent.jobContents[job.jobContentId]?.isFetching
    );
  });

  // This counter is used as a key to force a remount of all children to reset
  // their internal state when a template is applied. This is a temporary solution
  // to avoid reworking the input components which do not respond to state changes.
  const [templateAppliedTimes, templateAppliedCounter] = useCounter();
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false);

  const onSelectCustomer = useCallback(
    (customerId?: number) => {
      if (!job) return;
      dispatch(dealsActions.requestSetDealCustomer(job.dealId, customerId));
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'job'
    [job?.dealId, dispatch]
  );

  const jobIsInvoice = useMemo(() => {
    if (!job) return false;
    return isInvoice(job.status);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'job'
  }, [job?.status]);

  const onJobUpdateSuccess = useCallback(() => {
    dispatch(jobsActions.requestGetJob(jobId));
  }, [dispatch, jobId]);

  const onJobContentUpdateSuccess = useCallback(() => {
    if (!job?.jobContentId) return;
    dispatch(jobContentActions.requestGetJobContent(job.jobContentId));
    dispatch(
      jobContentActions.requestGetJobLineItemsForJobContent(job.jobContentId)
    );
    dispatch(dealsActions.requestGetDeal(job?.dealId));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'job.dealId'
  }, [dispatch, job?.jobContentId]);

  const jobsBelongingToDeal: Job[] = useSelector((state) =>
    deal ? getJobsByDealId(state, deal.id) : []
  );

  const canSelectCustomer: boolean = useMemo(() => {
    return canSetCustomerForDeal(jobsBelongingToDeal);
  }, [jobsBelongingToDeal]);

  useEffect(() => {
    dispatch(lineItemGroupActions.requestGetLineItemGroups());
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const onSelectContact = useCallback(
    (contactId: number) => {
      if (!job) return;

      dispatch(
        jobsActions.partialUpdateJob.request({
          jobId: job.id,
          data: { contactId },
          callback: () => dispatch(jobsActions.requestGetJob(job.id)),
        })
      );
    },
    [job, dispatch]
  );

  const requiresUpdateJob = useCallback(
    (updateJobRequestData: PartialUpdateJobRequestData) => {
      if (!job) return false;
      if (isFetchingJob || isUpdatingJob) return true;

      return getJobRequiresUpdate(job, updateJobRequestData);
    },
    [isFetchingJob, isUpdatingJob, job]
  );

  const requiresUpdateJobContent = useCallback(
    (formState: { [key: string]: any }) => {
      if (!jobContent) return false;
      if (isFetchingJobContent || isUpdatingJobContent) return true;
      return !isEqual(
        formState.jobContent,
        getInitialUpdateJobContentRequestDataFromBaseJobContent(jobContent)
      );
    },
    [isFetchingJobContent, isUpdatingJobContent, jobContent]
  );

  const updateJob = useCallback(
    (changes: { [key: string]: any }, onUpdateSuccess?: () => void) => {
      const updateJobRequestData = changes.job;
      const updateJobContentRequestData =
        changes.jobContent as UpdateJobContentRequestData;

      if (updateJobRequestData && requiresUpdateJob(updateJobRequestData)) {
        dispatch(
          jobsActions.partialUpdateJob.request({
            jobId: jobId,
            data: updateJobRequestData,
            callback: () => {
              onJobUpdateSuccess();
              onUpdateSuccess?.();
            },
          })
        );
      }
      if (
        updateJobContentRequestData &&
        updateJobContentRequestData.jobContentId
      ) {
        if (requiresUpdateJobContent(changes)) {
          dispatch(
            jobContentActions.requestUpdateJobContent(
              updateJobContentRequestData,
              () => {
                onJobContentUpdateSuccess();
                onUpdateSuccess?.();
              }
            )
          );
        }
      }
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'jobId', and 'dispatch'
    [
      onJobContentUpdateSuccess,
      onJobUpdateSuccess,
      requiresUpdateJobContent,
      requiresUpdateJob,
    ]
  );

  const sidebarContent = useMemo(() => {
    return (
      <>
        <div className="static-content-container">
          <EditJobPreviewButton
            jobId={jobId}
            proceedToPreview={proceedToPreview}
          />
        </div>
        <EditJobSaveFeedback jobId={jobId} />
        <div className="sidebar-scrollable-content-container">
          <JobSidePanel jobId={jobId} />
        </div>
      </>
    );
  }, [jobId, proceedToPreview]);

  const handleApplyTemplateSuccess = useCallback(() => {
    dispatch(jobsActions.clearJob(jobId));
    dispatch(jobsActions.requestGetJob(jobId));
    if (job?.dealId) {
      dispatch(dealsActions.clearDeal(job.dealId));
      dispatch(dealsActions.requestGetDeal(job.dealId));
    }
    templateAppliedCounter.inc();
  }, [dispatch, jobId, job?.dealId, templateAppliedCounter]);

  return (
    <ResponsiveViewWrapper
      key={`edit-job-control-${templateAppliedTimes}`}
      className="edit-job-control"
      downBreakpointSm={700}
      downBreakpointXs={450}
    >
      <BodyWithSidePanelContentWrapper sidebarContent={sidebarContent}>
        {/* Title section */}
        <div className="job-reference-feedback-wrapper pt-2">
          <JobReferenceControl jobId={jobId} updateJob={updateJob} />

          <PermissionGuard
            renderIfHasPermissions={[
              TemplatesPermissions.USE_PROPOSAL_TEMPLATES,
            ]}
          >
            <PLButton
              variant="white"
              size="sm"
              onClick={() => setShowApplyTemplateModal(true)}
              disabled={isUpdatingJob}
            >
              Apply template
            </PLButton>
          </PermissionGuard>
        </div>

        {/* Customer */}
        <JobCustomerControl
          canSelectCustomer={canSelectCustomer}
          customerId={deal?.customerId}
          onSelectCustomer={onSelectCustomer}
          jobContactId={job?.contactId || null}
          onSelectContact={onSelectContact}
          onPersistCustomerSuccess={() => {
            dispatch(jobsActions.requestGetJob(jobId));
          }}
        />

        {/* Intro - Job description */}
        <JobIntroControl jobId={jobId} updateJob={updateJob} />

        {/* Items */}
        {!!job &&
          (jobIsInvoice ? (
            <InvoiceLineItemsControl
              jobContentId={job.jobContentId}
              jobIsInvoice={jobIsInvoice}
            />
          ) : (
            <JobLineItemGroupsControl
              jobContentId={job.jobContentId}
              jobIsInvoice={jobIsInvoice}
            />
          ))}

        {/* Additional notes - Customer notes */}
        <JobAdditionalNotesControl jobId={jobId} updateJob={updateJob} />
        {job?.dealId && (
          <ProposalPaymentScheduleControl
            proposalPublicId={job.publicId}
            dealId={job.dealId}
            projectValue={jobContent?.total || 0}
            initialDepositPercentage={jobContent?.depositPercentage}
          />
        )}

        {/* Promo attachments */}
        <JobPromoAttachmentsControl jobId={jobId} />
      </BodyWithSidePanelContentWrapper>

      {job && deal && (
        <ApplyTemplateModal
          isOpen={showApplyTemplateModal}
          onClose={() => setShowApplyTemplateModal(false)}
          proposal={job}
          project={deal}
          onSuccess={handleApplyTemplateSuccess}
        />
      )}
    </ResponsiveViewWrapper>
  );
};
export default EditJobControl;

const ProposalPaymentScheduleControl: FC<{
  proposalPublicId: string;
  dealId: Deal['id'];
  projectValue: number;
  initialDepositPercentage?: number;
}> = (props) => {
  const { proposalPublicId, dealId, projectValue, initialDepositPercentage } =
    props;
  const deal = useDeal(dealId);
  const [showModifyPaymentScheduleModal, setShowModifyPaymentScheduleModal] =
    useState(false);

  const { paymentSchedule } = useGetProposalPaymentSchedule(proposalPublicId);

  const initialPaymentSchedule = useMemo(() => {
    return {
      stages: initialDepositPercentage
        ? [
            {
              percentageDue: initialDepositPercentage,
              description: 'Deposit',
              isDeposit: true,
            },
          ]
        : [],
      explainer: null,
    };
  }, [initialDepositPercentage]);

  if (!deal || deal.version < 2) return null;

  return (
    <>
      <EditJobSection
        title={
          <div className="flex flex-row items-baseline justify-between">
            <h2>Payment schedule</h2>
            {paymentSchedule && (
              <Button
                styleVariant={ButtonStyleVariant.ANCHOR}
                onClick={() => setShowModifyPaymentScheduleModal(true)}
              >
                Edit payment schedule
              </Button>
            )}
          </div>
        }
      >
        {paymentSchedule ? (
          <PaymentScheduleSummary
            paymentSchedule={paymentSchedule}
            projectValue={projectValue}
          />
        ) : (
          <Button
            styleVariant={ButtonStyleVariant.ANCHOR}
            onClick={() => setShowModifyPaymentScheduleModal(true)}
          >
            Set a payment schedule
          </Button>
        )}
      </EditJobSection>
      <Modal
        size="lg"
        isOpen={showModifyPaymentScheduleModal}
        onClose={() => setShowModifyPaymentScheduleModal(false)}
        title={'Edit payment schedule'}
      >
        <EditProposalPaymentScheduleModalBody
          proposalPublicId={proposalPublicId}
          paymentSchedule={paymentSchedule || initialPaymentSchedule}
          projectValue={projectValue}
          onSuccess={() => {
            setShowModifyPaymentScheduleModal(false);
          }}
        />
      </Modal>
    </>
  );
};

const EditProposalPaymentScheduleModalBody: FC<{
  proposalPublicId: string;
  paymentSchedule: PaymentSchedule;
  projectValue: number;
  onSuccess?: () => void;
}> = (props) => {
  const { proposalPublicId, paymentSchedule, projectValue, onSuccess } = props;
  const { mutate, isPending: isUpdating } = useUpdateProposalPaymentSchedule({
    proposalPublicId,
    options: { onSuccess },
  });

  const initialFormState = useMemo(() => {
    return { paymentSchedule };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'paymentSchedule'
  }, []);

  const fieldValidators = useMemo(() => {
    return {
      paymentSchedule: [getPaymentScheduleFieldValidator()],
    };
  }, []);

  const onSubmit = useCallback(
    (schedule: PaymentScheduleInput) => {
      const validStages = (schedule?.stages || [])
        .map((x) => ({ ...x, percentageDue: +x.percentageDue }))
        .filter((stage) => stage.description && stage.percentageDue);

      const cleanedPaymentSchedule = {
        explainer: validStages?.length ? schedule?.explainer : null,
        stages: validStages,
      };
      const paymentScheduleToSave = isPaymentScheduleValid(
        cleanedPaymentSchedule
      )
        ? cleanedPaymentSchedule
        : null;

      mutate({
        proposalId: proposalPublicId,
        paymentSchedule: paymentScheduleToSave,
      });
    },
    [proposalPublicId, mutate]
  );

  const onDelete = () => {
    mutate({
      proposalId: proposalPublicId,
      paymentSchedule: null,
    });
  };

  return (
    <ValidatedForm<{ paymentSchedule: PaymentScheduleInput }>
      fieldValidators={fieldValidators}
      initialFormState={initialFormState}
      renderFormContents={(
        isValid,
        formState,
        validationState,
        _touchedState,
        onFieldChange
      ) => {
        return (
          <>
            <Modal.Body>
              <CreatePaymentScheduleControl
                predictedProjectValue={projectValue}
                paymentSchedule={formState.paymentSchedule}
                onChange={(ps) => onFieldChange({ paymentSchedule: ps })}
                fieldValidationState={
                  isValid
                    ? undefined
                    : {
                        isValid: false,
                        validationMessages:
                          validationState.paymentSchedule?.errors,
                      }
                }
              />
            </Modal.Body>
            <Modal.Footer>
              <Modal.Footer.Actions>
                <PLButton
                  isProcessing={isUpdating}
                  onClick={() => onDelete()}
                  variant={'outline'}
                  colour={'red'}
                >
                  Delete
                </PLButton>
                <PLButton
                  isProcessing={isUpdating}
                  disabled={!isValid}
                  onClick={() => onSubmit(formState.paymentSchedule)}
                >
                  Save
                </PLButton>
              </Modal.Footer.Actions>
            </Modal.Footer>
          </>
        );
      }}
    />
  );
};
